import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { FC, memo, useCallback, useEffect, useState, useRef } from "react";
import OutsideClick from "../../outside-click/outside-click";
import PaginatedSearchableList from "../../selection-field/paginated-searchable-list";
import { Filter } from "../../../models/Filter";
import { Lookup } from "../../../models/Lookup";
import "./table-cell.css";
import { Direction } from "../../../constants/Direction";
import { DEF_PG_SIZE_FILTER } from "../../../constants/maximum-page-size";
import { useLocale } from "../../../contexts/localeContext";
import { useFilter } from "../../../contexts/table/FilterContext";
import { PaginationContext } from "../../../contexts/table/PaginationContext";
import { useSort } from "../../../contexts/table/SortContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import filterIcon from "../../../images/Filter.svg";
import columnSelectorIcon from "../../../images/Selector.svg";
import { PaginationModel } from "../../../models/Pagination";
import { TableColumn } from "../../../models/table/TableColumn";
import { buildSearchTerm } from "../../../utilities/SearchDescriptors";
import { matchesOptionValue } from "../../../utilities/genericUtil";
import { toast } from "react-toastify";

type Props = {
    head?: boolean;
    borderOptions?: string;
    direction?: string;
    onSort?: (id: any, direction: string) => void;
    searchOnName?: string;
    customise?: boolean;
    onCustomise?: (event) => void;
    columnId?: string;
    children?: any;
    columnData?: Lookup[];
    isMultiSelect?: boolean;
    columnFilters?: Filter[];
    alignmentOptions?: string;
    formattingOptions?: string;
    handleFilterSelection?: any;
    columnAttributes?: any;
};

const TableCell: FC<Props> = memo(
    ({
        head = false,
        borderOptions = "border",
        direction = undefined,
        onSort = () => {},
        searchOnName = undefined,
        customise = false,
        onCustomise = () => {},
        columnId,
        children,
        columnData = [],
        isMultiSelect = false,
        columnFilters = undefined,
        alignmentOptions = "px-3 py-1",
        formattingOptions = "",
        columnAttributes,
    }) => {
        const pageSize = DEF_PG_SIZE_FILTER;
        const [searchHovered, setSearchHovered] = useState(false);
        const [customHovered, setCustomHovered] = useState(false);
        const [sortByDirection, setSortByDirection] = useState(Direction.ascending);
        const [searchBoxOpen, setSearchBoxOpen] = useState(false);
        const [columns, setColumns] = useState<any[]>([]);
        const [cellPagination, setCellPagination] = useState<PaginationModel>({
            totalCount: 0,
            maxPageSize: pageSize,
            totalPages: 1,
            pageNumber: 1,
        });
        const [selectedItems, setSelectedItems] = useState<any[]>([]);
        const [lookup, setLookup] = useState<Lookup | undefined>(columnData.find((lookup) => searchOnName?.startsWith(lookup.name)));
        const [searchTerm, setSearchTerm] = useState("");
        const [isFilterClicked, setIsFilterClicked] = useState(false);
        const filterContext = useFilter();
        const isLoadingRef = useRef(false);
        const filterButtonRef = useRef<HTMLButtonElement>(null);
        const prevSearchTerm = useRef(searchTerm);
        const { translate } = useLocale();
        const prevPagination = useRef(cellPagination);
        const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

        useEffect(() => {
            if (!!searchOnName) {
                setLookup(columnData.find((lookup) => searchOnName?.startsWith(lookup.name)));
            }
        }, [columnData, searchOnName]);

        // Keep the filter dropdown on-screen and visually tied to the selected column header.
        // Position is recalculated on open, window resize, and scroll so fixed-position rendering
        // stays aligned with the filter icon.
        useEffect(() => {
            if (!searchBoxOpen) {
                return;
            }

            const updateDropdownPosition = () => {
                const rect = filterButtonRef.current?.getBoundingClientRect();
                if (!rect) {
                    return;
                }

                const dropdownWidth = 256;
                const viewportPadding = 8;
                const maxLeft = window.innerWidth - dropdownWidth - viewportPadding;
                // Keep the popup visually tied to the column by anchoring to the filter icon's right edge.
                const preferredLeft = rect.right - dropdownWidth;
                const left = Math.max(viewportPadding, Math.min(preferredLeft, maxLeft));

                setDropdownPosition({
                    top: rect.bottom + 6,
                    left,
                });
            };

            updateDropdownPosition();
            window.addEventListener("resize", updateDropdownPosition);
            window.addEventListener("scroll", updateDropdownPosition, true);

            return () => {
                window.removeEventListener("resize", updateDropdownPosition);
                window.removeEventListener("scroll", updateDropdownPosition, true);
            };
        }, [searchBoxOpen]);

        const fetchData = useCallback(async () => {
            if (isLoadingRef.current) {
                return;
            }

            isLoadingRef.current = true;
            try {
                const newSearchTerm = lookup?.searchAccessor && searchTerm ? buildSearchTerm(lookup.searchAccessor, searchTerm) : undefined;

                const { rows, pagination } = lookup?.dataMethod
                    ? await lookup.dataMethod({
                          goToPage: cellPagination.pageNumber,
                          pageSize: cellPagination.maxPageSize,
                          searchTerm: newSearchTerm,
                      })
                    : (() => {
                          const staticValues = lookup?.values ?? [];
                          const filteredValues = searchTerm
                              ? staticValues.filter((option: any) => {
                                    const label = `${option?.label ?? ""}`.toLowerCase();
                                    const value = `${option?.value ?? ""}`.toLowerCase();
                                    const term = searchTerm.toLowerCase();
                                    return label.includes(term) || value.includes(term);
                                })
                              : staticValues;

                          const totalCount = filteredValues.length;
                          const totalPages = Math.max(1, Math.ceil((totalCount || 1) / cellPagination.maxPageSize));
                          const currentPage = Math.min(cellPagination.pageNumber, totalPages);
                          const startIndex = (currentPage - 1) * cellPagination.maxPageSize;
                          const pagedValues = filteredValues.slice(startIndex, startIndex + cellPagination.maxPageSize);

                          return {
                              rows: pagedValues,
                              pagination: {
                                  ...cellPagination,
                                  pageNumber: currentPage,
                                  totalCount,
                                  totalPages,
                              },
                          };
                      })();

                if (rows) {
                    setColumns(rows);
                    setCellPagination(pagination);
                    const newSelectedItems = mapOptionsToSelectedIndexes(columnFilters, rows);
                    setSelectedItems(newSelectedItems ?? []);
                }
            } catch (error) {
                toast.warn(translate.byKey(TranslationKeys.loadFilterFailed));
            } finally {
                isLoadingRef.current = false;
                setIsFilterClicked(false);
            }
        }, [lookup, searchTerm, cellPagination, columnFilters, pageSize, translate]);

        useEffect(() => {
            const shouldFetch =
                isFilterClicked ||
                cellPagination.pageNumber !== prevPagination.current.pageNumber ||
                searchTerm !== prevSearchTerm.current;

            if (shouldFetch) {
                if (!isLoadingRef.current) {
                    fetchData();
                    prevPagination.current = cellPagination;
                    prevSearchTerm.current = searchTerm;
                }
                return;
            }

            prevPagination.current = cellPagination;
            prevSearchTerm.current = searchTerm;
        }, [fetchData, isFilterClicked, cellPagination]);

        useEffect(() => {
            if (filterContext.isInUse) {
                const newSelectedItems = mapOptionsToSelectedIndexes(filterContext.filters, columns);
                setSelectedItems(newSelectedItems);
            }
        }, [searchBoxOpen, filterContext.isInUse, filterContext.filters, columns]);

        const handleFilterClick = () => {
            setIsFilterClicked(true);
            setSearchBoxOpen((prev) => !prev);
        };

        const getSortByDirection = (currentDir) => {
            const newDirection = !currentDir || currentDir === Direction.descending ? Direction.ascending : Direction.descending;
            setSortByDirection(newDirection);
            return newDirection;
        };

        const mapOptionsToSelectedIndexes = (filters, newColumnOptions) => {
            const currentFilter = filters?.find((filter) => filter.key === searchOnName);
            return newColumnOptions.map((option) => currentFilter?.selected?.some((item) => matchesOptionValue(item.value, option.value)) ? option.value : undefined).filter((value) => value !== undefined);
        };

        const normalizeColumnId = (columnName: string): string => {
            return columnName.split(" ").join("");
        };

        const generateSortByColumnId = (columnName: string): string => {
            let id = normalizeColumnId(columnName);
            id = id.replace(id[0], id[0].toLowerCase());
            return id + "SortById";
        };

        const generateSortByColumnSearchId = (columnName: string): string => {
            let id = normalizeColumnId(columnName);
            id = id.replace(id[0], id[0].toLowerCase());
            return id + "SearchById";
        };

        const handleSearch = (newSearchTerm: string) => {
            setSearchTerm(newSearchTerm);
            setCellPagination((prev) => ({ ...prev, pageNumber: 1 }));
        };

        const sortContext = useSort();
        const [columnDescriptor, setColumnDescriptor] = useState<TableColumn>({
            accessor: "",
            active: false,
            heading: "",
            id: 0,
            isNumeric: false,
            translationKey: "",
        });

        const handleSorting = () => {
            if (sortContext?.isInUse) {
                sortContext.updateDirection(columnId);
            } else {
                onSort(columnId, getSortByDirection(sortByDirection));
            }
        };

        useEffect(() => {
            const newColumnDescriptor = sortContext.tableDescriptor.find((column) => column.id + "" === columnId + "");
            if (newColumnDescriptor) {
                setColumnDescriptor(newColumnDescriptor);
            }
        }, [columnId, sortContext.tableDescriptor]);

        const handleSelect = (index: number, value: any) => {
            const selectedItem = columns[index] ?? columns.find((item) => item.value === value);
            if (columnId !== undefined && columnId !== "" && selectedItem) {
                filterContext.updateFilters(
                    columnId,
                    selectedItem.value,
                    selectedItem.label,
                    undefined,
                    selectedItem.elementSysname,
                );
            }
        };

        const updatePageNumber = (pageNumber: number) => {
            setCellPagination((prev) => ({ ...prev, pageNumber }));
        };

        if (head) {
            return (
                <>
                    {columnAttributes?.active && (
                        <th
                            className={`${alignmentOptions} bg-gray-bar text-xs text-left font-normal ${borderOptions}`}
                            style={{ position: 'sticky', top: 0, zIndex: searchBoxOpen ? 30 : 20, overflow: 'visible' }}>
                            <div className="flex justify-between items-start">
                                <span className={customise ? "pt-px" : ""} id="tableHeaderId">
                                    {children}
                                </span>
                                <div className="flex flex-row flex-shrink-0 items-center">
                                    {sortContext.isInUse && columnDescriptor.sortDirection !== undefined ? (
                                        <FontAwesomeIcon
                                            className="mx-2 cursor-pointer"
                                            icon={columnDescriptor.sortDirection === Direction.descending ? faChevronUp : faChevronDown}
                                            onClick={handleSorting}
                                            id={generateSortByColumnId(children)}
                                        />
                                    ) : (
                                        !!direction && !!sortByDirection && (
                                            <FontAwesomeIcon
                                                className="mx-2 cursor-pointer"
                                                icon={sortByDirection === Direction.ascending ? faChevronDown : faChevronUp }
                                                onClick={handleSorting}
                                                id={generateSortByColumnId(children)}
                                            />
                                        )
                                    )}
                                    {!!searchOnName && !columnDescriptor.hideLookup && (
                                            <button
                                                ref={filterButtonRef}
                                                type="button"
                                                className="no-select border-none bg-transparent outline-none pr-2"
                                                onClick={handleFilterClick}
                                                onMouseEnter={() => setSearchHovered(true)}
                                                onMouseLeave={() => setSearchHovered(false)}>
                                                <img
                                                    className="search-column-icon"
                                                    src={filterIcon}
                                                    alt=""
                                                    id={generateSortByColumnSearchId(children)}
                                                />
                                            </button>
                                        )}
                                    {customise && (
                                        <button
                                            type="button"
                                            className="no-select border-none bg-transparent outline-none px-1 interaction-container -mt-px -mb-1 -mx-2"
                                            id="columnSelector"
                                            onClick={onCustomise}
                                            onMouseEnter={() => setCustomHovered(true)}
                                            onMouseLeave={() => setCustomHovered(false)}>
                                            <img
                                                className="customise-column-icon"
                                                src={columnSelectorIcon}
                                                alt=""
                                            />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <PaginationContext.Provider
                                value={{
                                    isInUse: true,
                                    pagination: cellPagination,
                                    updatePageNumber,
                                    updatePageSize: () => {},
                                }}>
                                {searchBoxOpen && (
                                    <OutsideClick onClick={() => setSearchBoxOpen(false)}>
                                        <div className="relative mx-1">
                                            <div className="fixed bg-white shadow-md rounded-md px-2 py-2 z-[999]" id="searchColumnFilterModel"
                                                style={{ top: dropdownPosition.top, left: dropdownPosition.left, width: "16rem", maxWidth: "calc(100vw - 16px)",}}>
                                                <PaginatedSearchableList
                                                    items={columns}
                                                    onSelect={handleSelect}
                                                    isMultiSelect={isMultiSelect}
                                                    selectedItems={selectedItems}
                                                    onSearch={handleSearch}
                                                    searchable={!!lookup?.searchAccessor}
                                                />
                                            </div>
                                        </div>
                                    </OutsideClick>
                                )}
                            </PaginationContext.Provider>
                        </th>
                    )}
                </>
            );
        } else {
            return (
                <>
                    {columnAttributes?.active && (
                        <td className={`${alignmentOptions} text-xs text-left ${borderOptions} ${formattingOptions}`}>
                            {children}
                        </td>
                    )}
                </>
            );
        }
    },
);

export default TableCell;

